<template>
  <div class="one-year-promotion-payment">
    <div class="summary">
      <img :src="require('@/assets/subscription/credit-card-header.svg')" alt="shapes image"/>
      <div class="summary-container margin-horizontal-ml">
        <div class="payment-card" :class="{ 'new-card-form': !useExitingCard }">
          <div class="ri-medium-bold-headline margin-bottom-xs">פרטי התשלום</div>
          <div v-if="!useExitingCard" class="use-existing-card-button">
            <div class="small-button" @click="onUseExistingCard">לשלם בכרטיס הקיים</div>
          </div>
          <div v-if="useExitingCard">
            <existing-credit-card-details
              :card-mask="formattedCardNumberMask"
              @otherCardClick="onEnterNewCard"
              class="compact-spacing"/>
            <div class="payment-separator"></div>
            <payment-details :price="promotionPrice" :payment-date="todayFormattedDate" :discount="false"
                             class="margin-bottom-xs"/>
          </div>
          <div v-else class="new-card-form">
            <credit-card-details-form-v1 :key="creditCardFormKey" ref="creditCardForm" :show-title="false" @init-failed="creditCardFormInitFailed" />
            <div class="payment-separator"></div>
            <payment-details :price="promotionPrice" :payment-date="todayFormattedDate" :discount="false"
                             class="margin-bottom-l"/>
          </div>
        </div>
      </div>
      <div class="promotion-benefits">
        <div class="benefit-item">
          <img src="@/assets/subscription/itemV.svg" alt="checkmark" class="checkmark-icon"/>
          <span class="benefit-text">
            {{ PROMOTION_CONSTANTS.EXTENSION_MONTHS }} חודשים מלאים בשירות.
            <strong>המינוי החדש יתחיל מתום המינוי הנוכחי, ב-{{ nextFormattedPaymentDate }}</strong>
          </span>
        </div>
        <div class="benefit-item">
          <img src="@/assets/subscription/itemV.svg" alt="checkmark" class="checkmark-icon"/>
          <span class="benefit-text">
            חיסכון של {{ PROMOTION_CONSTANTS.REGULAR_PRICE - PROMOTION_CONSTANTS.PROMO_PRICE }} ₪
            (תשלום של {{ PROMOTION_CONSTANTS.PROMO_PRICE }} ₪ במקום {{ PROMOTION_CONSTANTS.REGULAR_PRICE }} ₪)
          </span>
        </div>
        <div class="benefit-item">
          <img src="@/assets/subscription/itemV.svg" alt="checkmark" class="checkmark-icon"/>
          <span class="benefit-text">
            אפשר לבטל את המינוי בכל עת ולקבל החזר יחסי.
            <span class="terms-link" @click="openTerms">לתנאים המלאים</span>
          </span>
        </div>
      </div>

      <div class="separator"></div>

      <div class="payment-info">
        התשלום עבור המינוי החדש הוא מיידי
      </div>

      <div class="separator"></div>

      <div class="continuation-info tight-spacing">
        בתאריך {{ promotionEndDateFormatted }}, המינוי ימשיך אוטומטית<br>במחיר רגיל של {{ PROMOTION_CONSTANTS.REGULAR_PRICE }} ₪
      </div>

      <div class="padding-ml">
        <div class="result-container" v-if="processingCompleted">
          <div class="top">
            <div class="icon">
              <img class="icon" :src="getImageUrl()">
            </div>
            <div class="message" :class="{error: processingCompletedWithError, success: !processingCompletedWithError}">
              {{ resultMessage }}
            </div>
          </div>
          <div class="cta" v-if="processingCompletedWithError">
            אפשר לרענן את העמוד ולנסות להשתמש בכרטיס אחר.
          </div>
        </div>
        <riseup-button v-else :title="'אישור'" :size="'slim'" :action="onSubmit"
                       :disabled="submitLoading" :loading="submitLoading"/>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import BaseUI from '@riseupil/base-ui';
import PaymentDetails from '@/pages/responsive-pages/authenticated/subscription/PaymentDetails';
import CreditCardDetailsFormV1 from '@/pages/responsive-pages/authenticated/navigation-pages/payment/PaymentForm/CreditCardDetailsFormV1';
import ExistingCreditCardDetails from '@/pages/responsive-pages/authenticated/navigation-pages/payment/PaymentForm/ExistingCreditCardDetails';
import BillyApi from '@/api/BillyApi';
import DDLogs from '@/DDLogs';
import Segment from '@/Segment';
import moment from 'moment';
import PaymentHelper from '@/pages/responsive-pages/authenticated/payment/PaymentHelper';
import { PERSONAL_INFO_FIELDS } from '@/store/modules/payment';

const PAYMENT_PROCESSING_STATUSES = {
  NOT_STARTED: 'notStarted',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
};

const PROMOTION_CONSTANTS = {
  PROMO_PRICE: 430,
  REGULAR_PRICE: 528,
  EXTENSION_MONTHS: 12,
};

export default {
  name: 'OneYearPromotionPayment',
  mixins: [PaymentHelper],
  components: {
    PaymentDetails,
    CreditCardDetailsFormV1,
    ExistingCreditCardDetails,
    RiseupButton: BaseUI.RiseupButton,
  },
  props: {
    done: { type: Function, required: false, default: null },
    action: { type: Function, required: false, default: null },
  },
  async created() {
    await this.fetchPricing();
    // Load subscription data for nextPaymentDate
    await this.fetchSubscription();
    // Check if user has existing card
    await this.fetchCreditCardMask();
    this.useExitingCard = !!this.formattedCardNumberMask;

    // Ensure payment store is initialized for tokenization
    await this.initPaymeInterface();

    // Pre-fill personal info from active member if available
    if (this.activeMember) {
      const personalInfoData = {
        [PERSONAL_INFO_FIELDS.firstName]: {
          value: this.activeMember.firstNamePiiValue || '',
          isValid: !!(this.activeMember.firstNamePiiValue || '').trim(),
        },
        [PERSONAL_INFO_FIELDS.lastName]: {
          value: this.activeMember.lastNamePiiValue || '',
          isValid: !!(this.activeMember.lastNamePiiValue || '').trim(),
        },
        [PERSONAL_INFO_FIELDS.phoneNumber]: {
          value: this.activeMember.phoneNumberPiiValue || '',
          isValid: !!(this.activeMember.phoneNumberPiiValue || '').trim(),
        },
      };

      // Set the personal info using the action
      this.setPersonalInfo(personalInfoData);
    }
  },
  data() {
    return {
      submitLoading: false,
      useExitingCard: true,
      processingStatus: PAYMENT_PROCESSING_STATUSES.NOT_STARTED,
      creditCardFormKey: 0,
      PROMOTION_CONSTANTS,
    };
  },
  computed: {
    ...mapState('session', ['activeMember', 'customerId']),
    ...mapState('subscription', ['nextPaymentDate', 'pricing']),
    ...mapState('payment', ['tokenizationError', 'paymeToken', 'loadingToken', 'personalInfo', 'creditCardIsValid']),
    ...mapGetters('subscription', ['nextFormattedPaymentDate']),
    ...mapGetters('payment', ['formattedCardNumberMask']),
    ...mapGetters('session', ['householdName', 'activeMemberFirstName', 'activeMemberLastName', 'activeMemberPhoneNumber']),
    promotionPrice() {
      return PROMOTION_CONSTANTS.PROMO_PRICE;
    },
    promotionEndDateFormatted() {
      if (this.nextPaymentDate) {
        return moment(this.nextPaymentDate).add(1, 'year').format('DD.MM.YY');
      }
      return null;
    },
    todayFormattedDate() {
      return moment().format('DD.MM.YYYY');
    },
    resultMessage() {
      return this.processingCompletedWithError ? 'לא הצלחנו לחייב את הכרטיס' : 'התשלום עבר בהצלחה';
    },
    processingCompleted() {
      return this.processingStatus === PAYMENT_PROCESSING_STATUSES.COMPLETED;
    },
    processingCompletedWithError() {
      return this.processingCompleted && this.tokenizationError;
    },
    arePersonalInfoValid() {
      // Only require firstName and lastName to be valid, make phone optional
      const requiredFields = [PERSONAL_INFO_FIELDS.firstName, PERSONAL_INFO_FIELDS.lastName];
      const isValid = requiredFields.every(fieldName => {
        const field = this.personalInfo[fieldName];
        return field && field.isValid;
      });

      return isValid;
    },
  },
  methods: {
    ...mapActions('subscription', ['fetchPricing', 'fetchSubscription']),
    ...mapActions('payment', [
      'getOrCreatePaymentToken', 'fetchCreditCardMask', 'setPersonalInfoField',
      'setPaymeToken', 'initPaymeInterface', 'setPersonalInfo',
    ]),
    ...mapMutations('payment', ['setTokenizationError']),
    ...mapActions('timeLimitedOffers', ['deleteTimeLimitedOffer']),
    getImageUrl() {
      const iconType = this.processingCompletedWithError ? 'alert' : 'success';
      // eslint-disable-next-line global-require,import/no-dynamic-require
      return require(`@/assets/icons/${iconType}-icon.svg`);
    },
    async onSubmit() {
      if (this.processingStatus === PAYMENT_PROCESSING_STATUSES.IN_PROGRESS) {
        return;
      }

      // Track payment attempt
      Segment.trackUserInteraction('OneYearPromotionPayment_AttemptStarted', {
        useExistingCard: this.useExitingCard,
        price: PROMOTION_CONSTANTS.PROMO_PRICE,
      });

      this.processingStatus = PAYMENT_PROCESSING_STATUSES.IN_PROGRESS;
      this.submitLoading = true;

      // Clear previous errors
      this.setTokenizationError(null);

      try {
        // Get the card token - either existing card or new card (following PaymentFormPage pattern)
        let cardToken = null;
        if (!this.useExitingCard) {
          // First check if personal info is valid (like PaymentFormPage does)
          if (!this.arePersonalInfoValid) {
            this.setTokenizationError('Please fill in all required personal information');
            this.processingStatus = PAYMENT_PROCESSING_STATUSES.COMPLETED;
            this.submitLoading = false;
            return;
          }

          // Create token with promotion price using payment store's fieldsInstance
          const tokenCreated = await this.createPromotionPaymentToken();

          if (!tokenCreated) {
            // Tokenization failed - this will show the error through tokenizationError state
            this.processingStatus = PAYMENT_PROCESSING_STATUSES.COMPLETED;
            this.submitLoading = false;
            return;
          }
          // Check if we have a valid token (credit card validation not needed for custom tokenization)
          if (!this.paymeToken) {
            this.setTokenizationError('Credit card tokenization failed');
            this.processingStatus = PAYMENT_PROCESSING_STATUSES.COMPLETED;
            this.submitLoading = false;
            return;
          }
          cardToken = this.paymeToken.token; // Use the token property, not the whole object
        }

        const response = await BillyApi.purchaseYearlyPromoExtension({
          token: cardToken, // Use new card token or null for existing card
          email: this.activeMember.emailPiiValue,
        });

        // Handle success response
        if (response.result === 'success') {
          // Mark as purchased and delete the time-limited offer so it doesn't show again

          try {
            await this.deleteTimeLimitedOffer('one-year-promotion');
            // Refresh subscription data to get updated card information
            await this.fetchSubscription();
          } catch (deleteError) {
            // Don't fail the whole flow if deletion fails
          }

          // Navigate to success page or use provided action
          try {
            if (this.done) {
              const paymentMethodInfo = {
                usedExistingCard: this.useExitingCard,
              };
              await this.done(paymentMethodInfo);
            } else if (this.action) {
              await this.action();
            } else {
              await this.$router.push({ path: '/home' });
            }
          } catch (doneError) {
            // Error handled silently
          }
        } else {
          DDLogs.errorBusinessEvent('Failed purchasing One Year Promotion', {
            error: response.result,
            fullResponse: response,
          });

          // Track payment failure
          Segment.trackUserInteraction('OneYearPromotionPayment_Failed', {
            useExistingCard: this.useExitingCard,
            errorType: 'api_error',
            errorMessage: response.error || response.result || 'Payment failed',
            price: PROMOTION_CONSTANTS.PROMO_PRICE,
          });

          this.setTokenizationError(response.error || response.result || 'Payment failed');
          this.processingStatus = PAYMENT_PROCESSING_STATUSES.COMPLETED;
        }
      } catch (error) {
        DDLogs.errorBusinessEvent('Failed purchasing One Year Promotion', { error });

        // Track payment failure for exceptions
        Segment.trackUserInteraction('OneYearPromotionPayment_Failed', {
          useExistingCard: this.useExitingCard,
          errorType: 'exception',
          errorMessage: error.message || error || 'Payment failed',
          price: PROMOTION_CONSTANTS.PROMO_PRICE,
        });

        this.setTokenizationError(error.message || error || 'Payment failed');
        this.processingStatus = PAYMENT_PROCESSING_STATUSES.COMPLETED;
      } finally {
        this.submitLoading = false;
      }
    },
    openTerms() {
      window.open('https://www.riseup.co.il/termsofuse', '_blank');
    },
    onEnterNewCard() {
      this.useExitingCard = false;
    },
    onUseExistingCard() {
      this.useExitingCard = true;
    },
    creditCardFormInitFailed() {
      // Handle credit card form initialization failure
      // Could show error message or fallback behavior
    },
    async createPromotionPaymentToken() {
      try {
        const { customerId } = this.$store.state.session;
        const sale = {
          payerFirstName: this.personalInfo[PERSONAL_INFO_FIELDS.firstName].value,
          payerLastName: this.personalInfo[PERSONAL_INFO_FIELDS.lastName].value,
          payerPhone: this.personalInfo[PERSONAL_INFO_FIELDS.phoneNumber].value.replace('-', ''),
          payerEmail: `${customerId}@riseup.co.il`,
          total: {
            label: 'תשלום לרייזאפ - מינוי שנתי מוזל',
            amount: {
              currency: 'ILS',
              value: `${PROMOTION_CONSTANTS.PROMO_PRICE}.00`,
            },
          },
        };

        const tokenRequest = await this.$store.state.payment.fieldsInstance.tokenize(sale);
        const { token, card } = tokenRequest;
        this.$store.commit('payment/setPaymeToken', { token, card });
        return true;
      } catch (error) {
        this.setTokenizationError(error);
        return false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-colors';

.one-year-promotion-payment {
  height: 100%;

  .summary {
    display: flex;
    flex-direction: column;
    height: 100%;

    .summary-container {
      flex: 1;
    }
  }

  .payment-card {
    border-radius: 20px;
    border: 1px solid #E8E6E3;
    background: #FDFDFD;
    box-shadow: 0 4px 12.6px 0 rgba(0, 0, 0, 0.06);
    width: 352px;
    height: 258px;
    flex-shrink: 0;
    margin: 24px auto 0 auto;
    padding: 16px;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    box-sizing: border-box;

    &.new-card-form {
      height: auto;
      min-height: 258px;
    }
  }

  .payment-separator {
    width: 302px;
    height: 1px;
    background-color: #E5E5E5;
    margin: 8px auto;
  }

  .promotion-benefits {
    padding: 12px 14px;
    margin-bottom: 0;

    .benefit-item {
      display: flex;
      align-items: flex-start;
      margin-bottom: 8px;
      text-align: right;
      direction: rtl;

      &:first-child {
        margin-top: 15px;
      }

      .checkmark-icon {
        width: 20px;
        height: 20px;
        margin-left: 8px;
        flex-shrink: 0;
      }

      .benefit-text {
        font-size: 16px;
        line-height: 1.4;
        color: #202125;

        .terms-link {
          color: #5D7AFD;
          text-decoration: underline;
          cursor: pointer;

          &:hover {
            color: #4A67D1;
          }
        }
      }
    }
  }

  .separator {
    height: 1px;
    background-color: #E5E5E5;
    margin: 10px 22px;
  }

  .info-text-base {
    color: var(--Black, #000);
    text-align: right;
    font-feature-settings: 'liga' off, 'clig' off;
    font-family: SimplerPro;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    margin: 10px 0;
    direction: rtl;
    padding-right: 12px;
  }

  .renewal-info {
    @extend .info-text-base;
  }

  .payment-info {
    @extend .info-text-base;
  }

  .continuation-info {
    @extend .info-text-base;

    &.tight-spacing {
      line-height: 1.2;
    }
  }

  .small-button {
    cursor: pointer;
    font-size: 14px;
    text-decoration: underline;
    color: $riseup_blue;
    text-align: center;
    margin: 16px 0;
    padding: 8px 0;
    font-weight: 500;

    &:hover {
      color: darken($riseup_blue, 10%);
    }
  }

  .compact-spacing {
    padding-bottom: 10px;
  }

  .use-existing-card-button {
    margin-bottom: 16px;
    text-align: center;

    .small-button {
      text-decoration: none;
      width: fit-content;
    }
  }

  .new-card-form {
    animation: fadeIn 0.3s ease-in-out;

    .credit-card-details-form-v1 {
      margin-bottom: 8px;
    }

    .payment-details {
      margin-bottom: 8px;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .result-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 18px 16px;
    border-radius: 3px;
    background-color: rgba($riseup_gray_0, 0.4);
    margin-bottom: 16px;
  }

  .padding-ml {
    display: flex;
    flex-direction: column;
    align-items: center;

    .riseup-button {
      margin: 0 auto;
    }

    .top {
      display: flex;
      flex-direction: row;
      align-items: center;
      font-weight: bold;
      font-size: 20px;
      color: $riseup_danger_red;
      margin-left: 20px;

      .icon {
        margin-left: 11px;
      }

      .message {
        font-size: 20px;
        font-weight: bold;

        &.error {
          color: $riseup_danger_red;
        }

        &.success {
          color: $riseup_green;
        }
      }
    }

    .cta {
      margin-top: 5px;
      font-family: SimplerPro;
      font-size: 16px;
      line-height: 1.5;
    }
  }
}
</style>
